import React, { createContext, useContext, useState } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import LoginPage from './pages/LoginPage';
import PasswordResetPage from './pages/PasswordResetPage';
import StammdatenPage from './pages/StammdatenPage';
import ProduktePage from './pages/ProduktePage';
import RechnungenPage from './pages/RechnungenPage';
import BestellungPage from './pages/BestellungPage';
import DashboardPage from './pages/DashboardPage';
import MainLayout from './components/MainLayout';

const isTokenCurrent = (value: string | null) => {
  if (!value) return false;
  try {
    const payload = JSON.parse(atob(value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('token');
    if (isTokenCurrent(storedToken)) return storedToken;
    localStorage.removeItem('token');
    return null;
  });

  React.useEffect(() => {
    if (!token) return undefined;
    const expiry = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp * 1000;
    const timer = window.setTimeout(() => {
      localStorage.removeItem('token');
      setToken(null);
    }, Math.max(0, expiry - Date.now()));
    return () => window.clearTimeout(timer);
  }, [token]);

  const login = (newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated: Boolean(token), login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb des AuthProvider verwendet werden');
  return context;
};

const RequireAuth: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

const theme = createTheme({
  palette: { primary: { main: '#4277fc' } },
});

const App: React.FC = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/passwort-zuruecksetzen" element={<PasswordResetPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/stammdaten" element={<StammdatenPage />} />
              <Route path="/bestellung" element={<BestellungPage />} />
              <Route path="/produkte" element={<ProduktePage />} />
              <Route path="/rechnungen" element={<RechnungenPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
